/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, type ReactElement } from 'react';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';

const FAQS = [
    {
        category: 'Orders & Shipping',
        items: [
            {
                q: 'How do I check my order status?',
                a: 'You can check your order status by signing into your Baggallini account or using the tracking number from your confirmation email. Make sure to allow emails from Baggallini so shipping updates reach your inbox.',
            },
            {
                q: "Why hasn't my order shipped yet?",
                a: 'Tracking updates typically appear within 24–48 hours of shipment. Ground orders ship via DHL; expedited orders ship via FedEx. During busy periods or severe weather, additional delays may occur.',
            },
            {
                q: "I didn't receive a confirmation email — what should I do?",
                a: 'Confirmation emails are sent shortly after your order is placed. Please check your spam or junk folder and add Baggallini to your allowed senders. We also send shipping and delivery updates to the same address.',
            },
            {
                q: 'Do you offer international shipping?',
                a: 'No, we currently do not offer international shipping. Orders are delivered within the United States only.',
            },
        ],
    },
    {
        category: 'Returns & Refunds',
        items: [
            {
                q: 'What is your return policy?',
                a: 'Unused merchandise purchased through baggallini.com is eligible for a full refund within 30 days of date of purchase. Items must be in their original, unused condition. Items marked as Final Sale are not eligible for return, refund, or exchange. We reserve the right to approve or refuse returns that do not meet our policy requirements.',
            },
            {
                q: 'How do I make a return?',
                a: 'Sign in to your account or visit our returns page and enter your order number, email address, and billing zip code to initiate a return. Once approved, a prepaid return label will be emailed to you.',
            },
            {
                q: 'Can I return a purchase I made at a retail store?',
                a: 'Baggallini does not accept returns or exchanges on products purchased at department stores or specialty retail stores. Please contact the retailer directly for their return policy.',
            },
            {
                q: 'How long does it take to receive my refund?',
                a: 'Once we receive and process your return, please allow 5–10 business days for the refund to appear on your original payment method. Original shipping charges are non-refundable.',
            },
        ],
    },
    {
        category: 'Products & Features',
        items: [
            {
                q: 'Do Baggallini bags have RFID-blocking pockets?',
                a: 'Many Baggallini bags include RFID-blocking pockets to protect your credit cards and passport from electronic theft. Check the individual product page for details on which styles include this feature.',
            },
            {
                q: 'What is the Securtex® anti-theft collection?',
                a: 'The Securtex® line offers slash-resistant straps, locking zippers, and water-resistant materials for added security. It is especially recommended for travel to high-theft areas. Specific Securtex® models are also designed to comply with airline personal item size requirements.',
            },
            {
                q: 'How do I care for my Baggallini bag?',
                a: 'Spot-clean your bag with a damp cloth and mild soap. Avoid machine washing and do not use bleach. Allow the bag to air dry completely before storing.',
            },
            {
                q: 'How long do Baggallini bags last?',
                a: 'Many customers carry the same Baggallini bag for four or more years. Our bags are built with lightweight, durable materials designed to hold up through everyday use and travel.',
            },
        ],
    },
    {
        category: 'Promotions & Discounts',
        items: [
            {
                q: "My discount code isn't working — why?",
                a: 'Discount codes apply to new orders only, one code per order, and cannot be combined with other offers. Codes may also expire. You can find current offers on our homepage, in promotional emails and texts, or through mailed offers.',
            },
            {
                q: 'Where can I find current promotions?',
                a: 'Current promotions are listed on our homepage banner. You can also sign up for our email list to receive exclusive offers directly in your inbox. Sale items are available in the Sale section of our website.',
            },
        ],
    },
];

function FaqItem({ q, a }: { q: string; a: string }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-[#e8e8e8]">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-5 text-left gap-4 cursor-pointer"
                aria-expanded={open}>
                <span className="font-medium text-foreground">{q}</span>
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && <p className="pb-5 text-muted-foreground leading-relaxed text-sm pr-8">{a}</p>}
        </div>
    );
}

export default function FAQ(): ReactElement {
    return (
        <div className="pb-16">
            <SeoMeta
                title="Frequently Asked Questions — Baggallini"
                description="Find answers to common questions about Baggallini orders, returns, shipping, anti-theft features, bag care, and more from our customer support team."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>FAQ</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <div className="max-w-screen-md mx-auto px-6 pt-10">
                <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-5xl font-light tracking-wide mb-3">
                    Frequently Asked Questions
                </h1>
                <p className="text-muted-foreground mb-10 max-w-xl">
                    Find quick answers below, or reach our team directly for personalized help.
                </p>

                <div className="mb-14 space-y-10">
                    {FAQS.map((group) => (
                        <div key={group.category}>
                            <h2
                                style={{ fontFamily: 'var(--font-display)' }}
                                className="text-2xl font-light mb-2 text-foreground border-b border-[#e8e8e8] pb-3">
                                {group.category}
                            </h2>
                            {group.items.map((faq) => (
                                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
                            ))}
                        </div>
                    ))}
                </div>

                {/* Contact strip */}
                <div className="bg-[#F5F3F0] rounded-2xl px-8 py-8 flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="flex-1">
                        <p className="text-sm font-medium tracking-[0.1em] uppercase mb-1">Still need help?</p>
                        <p className="text-muted-foreground text-sm">
                            Our customer service team is available Mon–Fri, 9am–5pm ET.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 text-sm shrink-0">
                        <a href="tel:18004488753" className="font-medium text-foreground hover:underline">
                            1-800-448-8753
                        </a>
                        <Link
                            to="/contact-us"
                            className="inline-flex items-center justify-center bg-foreground text-background text-sm tracking-[0.1em] uppercase font-medium px-6 py-3 hover:opacity-80 transition-opacity rounded-none">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
